/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps } from '@msdyn365-commerce/core';
import { Button } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import { BusinessPartner, BusinessPartnersDataActions } from '@msdyn365-commerce/retail-proxy';
import { IBusinessPartnersResources } from '../business-partners.props.autogenerated';
import { DataGrid, IDataGridColumn } from './data-grid';

/**
 * Props of IBusinessPartnersTable.
 */
export interface IBusinessPartnersTableProps extends IComponentProps {
    className: string;
    pageSize?: number;
    resources: IBusinessPartnersResources;
    onSelect(businessPartner: BusinessPartner): void;
}

/**
 * BusinessPartnersTable component.
 */
export class BusinessPartnersTable extends React.Component<IBusinessPartnersTableProps> {
    public shouldComponentUpdate(nextProps: Readonly<IBusinessPartnersTableProps>): boolean {
        return this.props.id !== nextProps.id;
    }

    public render(): JSX.Element {
        return (
            <DataGrid
                {...this.props}
                loadData={this.getBusinessPartners}
                columns={this.getColumns()}
                defaultSorting={{ fieldName: 'Name', ascending: true }}
                searchPlaceholderLabel={this.props.resources.businessPartnerSearchCompany}
            />
        );
    }

    /**
     * Get column definition of the table.
     * @returns - Columns.
     */
    private readonly getColumns = (): IDataGridColumn<BusinessPartner>[] => {
        return [
            {
                reactKey: 'name',
                name: this.props.resources.businessPartnerName,
                fieldName: 'Name',
                isSearchable: true
            },
            {
                reactKey: 'select-button',
                name: '',
                onRender: this.renderSelectButton,
                className: `${this.props.className}__row__column-select-button`
            }
        ];
    };

    /**
     * Renders the button to select a business partner.
     * @param businessPartner - The business partner to select.
     * @returns - Button component.
     */
    private readonly renderSelectButton = (businessPartner: BusinessPartner) => {
        return (
            <Button
                className={`${this.props.className}__row__select-button`}
                onClick={() => {
                    this.props.onSelect(businessPartner);
                }}
                title={this.props.resources.select}
                aria-label={this.props.resources.select}
            />
        );
    };

    /**
     * Call retail proxy to get business partners.
     * @returns - Business partners.
     */
    private readonly getBusinessPartners = async () => {
        const businessPartners = await BusinessPartnersDataActions.getManagedBusinessPartnersAsync({
            callerContext: this.props.context.actionContext,
            bypassCache: 'get',
            queryResultSettings: {}
        });
        return businessPartners;
    };
}
